import * as React from 'react';

import {
  DataSourceTable,
  getDataSourceTableKey,
} from 'src/constants/dataSourceTable';

type UseTableCheckingProps = {
  tables: DataSourceTable[];
  setCheckedVgTables: React.Dispatch<React.SetStateAction<DataSourceTable[]>>;
};

type UseTableCheckingResult = {
  handleCheckTable: (tableKey: string) => void;
  handleCheckAllInCatalog: (catalog: string, tableKeys: string[]) => void;
  handleCheckAllInSchema: (
    catalog: string,
    schema: string,
    tableKeys: string[]
  ) => void;
};

export const useTableChecking = ({
  tables,
  setCheckedVgTables,
}: UseTableCheckingProps): UseTableCheckingResult => {
  const handleCheckTable = React.useCallback(
    (tableKey: string) => {
      setCheckedVgTables((prev) => {
        const isChecked = prev.some(
          (t) => getDataSourceTableKey(t) === tableKey
        );
        if (isChecked) {
          return prev.filter((t) => getDataSourceTableKey(t) !== tableKey);
        }
        const table = tables.find((t) => getDataSourceTableKey(t) === tableKey);
        if (table) {
          return [...prev, table];
        }
        return prev;
      });
    },
    [tables, setCheckedVgTables]
  );

  const toggleAllTables = React.useCallback(
    (tableKeys: string[]) => {
      setCheckedVgTables((prev) => {
        const prevKeys = prev.map((t) => getDataSourceTableKey(t));
        const allChecked = tableKeys.every((key) => prevKeys.includes(key));

        if (allChecked) {
          return prev.filter(
            (t) => !tableKeys.includes(getDataSourceTableKey(t))
          );
        }
        const newTables = tables.filter(
          (t) =>
            tableKeys.includes(getDataSourceTableKey(t)) &&
            !prevKeys.includes(getDataSourceTableKey(t))
        );
        return [...prev, ...newTables];
      });
    },
    [tables, setCheckedVgTables]
  );

  const handleCheckAllInCatalog = React.useCallback(
    (_catalog: string, tableKeys: string[]) => toggleAllTables(tableKeys),
    [toggleAllTables]
  );

  const handleCheckAllInSchema = React.useCallback(
    (_catalog: string, _schema: string, tableKeys: string[]) =>
      toggleAllTables(tableKeys),
    [toggleAllTables]
  );

  return {
    handleCheckTable,
    handleCheckAllInCatalog,
    handleCheckAllInSchema,
  };
};
